import React, { useState } from 'react';

import { Autocomplete, TextField } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, getStatusChipText, WorkflowStatusChip } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
} from './autocomplete-filter-utils';

const useStyles = makeStyles()(theme => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  tag: {
    marginLeft: theme.spacing(1),
  },
}));

export function WorkflowStatusAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const { classes } = useStyles();
  const [values, setValues] = useState<Apollo.WorkflowStatus[]>(item.value ?? []);
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: Apollo.WorkflowStatus[]): void {
    applyValue({
      ...item,
      value,
    });
    setValues(value);
  }

  return (
    <Autocomplete<Apollo.WorkflowStatus, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={option => getStatusChipText(option)}
      inputValue={inputValue}
      options={[Apollo.WorkflowStatus.Running, Apollo.WorkflowStatus.Completed]}
      renderInput={params => {
        return <TextField placeholder="Search statuses..." {...params} variant="standard" />;
      }}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} />
            <WorkflowStatusChip status={option} />
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <>
            {tags.map((tag, index) => {
              return <WorkflowStatusChip key={index} className={classes.tag} status={tag} />;
            })}
          </>
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={values}
      onChange={(_e, values) => handleValueChange(values)}
      onInputChange={onInputChange}
    />
  );
}
